/**
 * Profile Setup Modal - First-time user setup for avatar and display name
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { User, Upload, X, Check, Mail, LogOut } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
import * as usersApi from '../../lib/mudoNetwork/usersApi';
import { APP_BASE_URL } from "@/lib/env";
import { CHARACTER_AVATARS } from '../../lib/avatars';
import { persistAvatarForCurrentUser } from '../../lib/avatarPersistence';

const AVATAR_EMOJIS = [
  '🎨', '🏺', '🖌️', '✨', '🌟', '💫', '🔥', '💎', 
  '🌈', '🦋', '🌸', '🌺', '🍄', '🌿', '🪴', '🌵',
  '🐱', '🐶', '🐼', '🦊', '🐨', '🦁', '🐯', '🐸',
  '😊', '😎', '🤓', '🥳', '😇', '🤩', '😌', '🙂'
];

export default function ProfileSetupModal({ isOpen, onComplete, onClose }) {
  const navigate = useNavigate();
  const { profile, refreshProfile } = useAuth() || {};
  const [step, setStep] = useState(0); // 0: email login, 1: avatar, 2: info
  const [displayName, setDisplayName] = useState(profile?.display_name || '');
  const [email, setEmail] = useState(profile?.email || '');
  const [location, setLocation] = useState(profile?.country || profile?.location || '');
  const [selectedEmoji, setSelectedEmoji] = useState(profile?.avatar_emoji || '🎨');
  const [uploadedImage, setUploadedImage] = useState(null);
  const [selectedCharacterUrl, setSelectedCharacterUrl] = useState(
    profile?.avatar_url && String(profile?.avatar_url).startsWith('/Avatars/')
      ? profile.avatar_url
      : null
  );
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [avatarType, setAvatarType] = useState(() => {
    const url = profile?.avatar_url;
    if (typeof url === 'string' && url.startsWith('/Avatars/')) return 'character';
    if (typeof url === 'string' && url.length > 0) return 'upload';
    return 'emoji';
  }); // 'emoji' | 'upload' | 'character'
  const [loginSent, setLoginSent] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [cooldownUntil, setCooldownUntil] = useState(0);

  const fireToast = (message, tone = "info", ttl = 2200) => {
    try {
      window.dispatchEvent(
        new CustomEvent("mudo:toast", { detail: { message, tone, ttl } })
      );
    } catch {
      // ignore
    }
  };

  // Force close handler
  const handleClose = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    console.log('handleClose called, onClose type:', typeof onClose, 'value:', onClose);
    setStep(0);
    setLoginSent(false);
    setLoginError('');
    setOtpCode('');
    if (typeof onClose === 'function') {
      console.log('Calling onClose');
      onClose();
    } else {
      console.error('onClose is not a function! Type:', typeof onClose, 'Value:', onClose);
      // Force close by trying to manipulate DOM as last resort
      const modalElement = document.querySelector('[data-profile-modal]');
      if (modalElement) {
        modalElement.style.display = 'none';
      }
    }
  };

  // Check if user is authenticated with Supabase
  useEffect(() => {
    async function checkAuth() {
      const { data: { user } } = await supabase.auth.getUser();
      setIsLoggedIn(!!user?.id);
      if (user?.email && !email) setEmail(user.email);
      if (!!user?.id && step === 0) setStep(1); // advance to avatar step if already logged in
    }
    if (isOpen) checkAuth();
  }, [isOpen, step]);

  // Keep fields in sync if profile loads/refreshes while modal is open.
  useEffect(() => {
    if (!isOpen) return;
    setDisplayName((prev) => prev || profile?.display_name || "");
    setLocation((prev) => prev || profile?.country || profile?.location || "");
    setEmail((prev) => prev || profile?.email || "");
    if (profile?.avatar_emoji && avatarType === 'emoji') {
      setSelectedEmoji((prev) => prev || profile.avatar_emoji);
    }
    const url = profile?.avatar_url;
    if (typeof url === 'string' && url.startsWith('/Avatars/')) {
      setSelectedCharacterUrl((prev) => prev || url);
      if (!uploadedImage) setAvatarType('character');
    } else if (typeof url === 'string' && url.length > 0 && !uploadedImage) {
      setUploadedImage((prev) => prev || url);
      setAvatarType('upload');
    }
  }, [isOpen, profile]);

  // Close on Esc
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen]);

  // Handle email login (magic link)
  const handleEmailLogin = async () => {
    setLoginError('');
    if (Date.now() < cooldownUntil) {
      const waitMs = Math.max(0, cooldownUntil - Date.now());
      setLoginError(`Please wait ${(waitMs / 1000).toFixed(0)}s before requesting another link.`);
      return;
    }
    if (!email.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setLoginError('Please enter a valid email address.');
      return;
    }
    try {
      // Use environment variable for redirect URL, fallback to window.location.origin
      const redirectUrl = APP_BASE_URL || window.location.origin;
      
      const { error } = await supabase.auth.signInWithOtp({
        email: email.trim(),
        options: {
          emailRedirectTo: redirectUrl,
        },
      });
      if (error) {
        console.error('Supabase auth error:', error);
        throw error;
      }
      setLoginSent(true);
      setOtpCode('');
      setCooldownUntil(Date.now() + 20000); // avoid Supabase 429 spam
    } catch (err) {
      console.error('Email login error:', err);
      setLoginError(err.message || 'Failed to send login link. Please try again.');
    }
  };

  const handleVerifyCode = async () => {
    setLoginError('');
    if (!email.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setLoginError('Please enter a valid email address.');
      return;
    }
    if (!otpCode.trim()) {
      setLoginError('Enter the 6-digit code from your email.');
      return;
    }
    try {
      setVerifying(true);
      // Use email OTP (6-digit code). Fallback to magiclink hash if provider expects it.
      const payload = { email: email.trim(), token: otpCode.trim(), type: 'email' };
      let { data, error } = await supabase.auth.verifyOtp(payload);
      if (error) {
        console.warn('[ProfileSetup] verifyOtp email failed, retry with magiclink', error);
        ({ data, error } = await supabase.auth.verifyOtp({
          email: email.trim(),
          token: otpCode.trim(),
          type: 'magiclink',
        }));
      }
      if (error) throw error;
      console.log('[ProfileSetup] verifyOtp success', { data });
      setLoginSent(false);
      setIsLoggedIn(true);
      setStep(1);
    } catch (err) {
      console.error('Verify code error:', err);
      setLoginError(err.message || 'Invalid or expired code. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      alert('Image must be less than 2MB');
      return;
    }

    setUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
  // Get current user ID from Supabase
  const { data: { user } } = await supabase.auth.getUser();
  if (!user?.id) throw new Error('Not authenticated');
  const fileName = `${user.id}/${Date.now()}.${fileExt}`;

      const { data, error } = await supabase.storage
        .from('avatars')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true,
        });

      if (error) throw error;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName);

      setUploadedImage(publicUrl);
      setAvatarType('upload');
    } catch (err) {
      console.error('Error uploading avatar:', err);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!displayName.trim()) {
      fireToast('Please enter a display name', 'warn');
      return;
    }
    if (!email.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      fireToast('Please enter a valid email address', 'warn');
      return;
    }
    if (!location.trim()) {
      fireToast('Please enter your location', 'warn');
      return;
    }

    setSaving(true);
    try {
      const avatarUrl =
        avatarType === 'upload'
          ? uploadedImage
          : avatarType === 'character'
          ? selectedCharacterUrl
          : null;
      const avatarEmoji = avatarType === 'emoji' ? selectedEmoji : null;

      const updates = {
        display_name: displayName.trim(),
        country: location.trim(),
        avatar_url: avatarUrl,
        avatar_emoji: avatarEmoji,
      };
      const { data, error } = await usersApi.updateUserProfile(updates);
      if (error) throw error;

      // Keep AuthContext tables in sync so avatars persist across refresh everywhere.
      await persistAvatarForCurrentUser({
        avatarUrl,
        avatarEmoji,
        displayName: updates.display_name,
        country: updates.country,
      });

      // Optionally update local profile
      if (refreshProfile) await refreshProfile();
      try {
        localStorage?.setItem('mudo:userName', updates.display_name);
        localStorage?.setItem('mudo:userCountry', updates.country);
      } catch {
        // ignore
      }
      fireToast('Profile saved', 'success');
      onComplete?.();
    } catch (err) {
      console.error('Error saving profile:', err);
      fireToast(err?.message || 'Failed to save profile', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Add logout handler
  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      setIsLoggedIn(false);
      handleClose();
    } catch (err) {
      alert('Failed to log out. Please try again.');
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      data-profile-modal
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={(e) => {
        // Close when clicking backdrop (not the modal content)
        if (e.target === e.currentTarget) {
          handleClose();
        }
      }}
    >
      <div className="bg-[#1e1f22] border border-white/10 rounded-2xl shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <h2 className="text-xl font-bold text-white">
            {step === 0 ? 'Sign In to Mudo' : step === 1 ? 'Choose Your Avatar' : 'Complete Your Profile'}
          </h2>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleClose(e);
            }}
            className="p-2 rounded-lg hover:bg-white/10 text-white/70 hover:text-white transition-colors"
            aria-label="Close setup"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {step === 0 ? (
            <>
              <div className="mb-6 text-center">
                <Mail className="w-12 h-12 mx-auto text-green-500 mb-2" />
                <div className="text-lg text-white font-semibold mb-2">Enter your email to sign in</div>
                <div className="text-sm text-white/60 mb-4">We'll send you a magic link to log in to Mudo.</div>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-green-500/50 mb-3"
                  disabled={loginSent}
                />
                <button
                  onClick={handleEmailLogin}
                  className="w-full px-4 py-3 rounded-lg bg-green-600 hover:bg-green-700 text-white font-semibold transition-colors mb-2"
                  disabled={loginSent}
                >
                  {loginSent ? 'Check your email!' : 'Send Login Link'}
                </button>
                {loginError && <div className="text-red-400 text-sm mt-2">{loginError}</div>}
              {loginSent && (
                <div className="text-green-400 text-sm mt-2">
                  A login link has been sent to your email. Please check your inbox and click the link to continue.
                </div>
              )}

              {loginSent && (
                <div className="mt-4 border border-white/10 rounded-lg p-3 bg-white/5">
                  <div className="text-white text-sm font-semibold mb-2">Or enter the code from your email</div>
                  <input
                    type="text"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value)}
                    placeholder="6-digit code"
                    className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/10 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-green-500/50 mb-2"
                    maxLength={10}
                  />
                  <button
                    onClick={handleVerifyCode}
                    disabled={verifying}
                    className="w-full px-3 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white font-semibold transition-colors disabled:opacity-60"
                  >
                      {verifying ? 'Verifying...' : 'Verify code'}
                  </button>
                </div>
              )}
            </div>
            </>
          ) : step === 1 ? (
            <>
              {/* Avatar Selection */}
              <div className="text-center mb-6">
                <div className="w-32 h-32 mx-auto mb-4 rounded-full bg-white/5 border-2 border-white/10 flex items-center justify-center text-6xl overflow-hidden">
                  {avatarType === 'emoji' ? (
                    selectedEmoji
                  ) : avatarType === 'character' && selectedCharacterUrl ? (
                    <img src={selectedCharacterUrl} alt="Avatar" className="w-full h-full object-cover" />
                  ) : uploadedImage ? (
                    <img src={uploadedImage} alt="Avatar" className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-16 h-16 text-white/30" />
                  )}
                </div>
              </div>

              {/* Upload Button */}
              <div className="mb-6">
                <label className="block w-full">
                  <div className="px-4 py-3 rounded-lg bg-blue-600/20 hover:bg-blue-600/30 border border-blue-500/30 text-blue-300 font-semibold text-center cursor-pointer transition-colors">
                    <Upload className="w-5 h-5 inline-block mr-2" />
                    {uploading ? 'Uploading...' : 'Upload Photo'}
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={uploading}
                    className="hidden"
                  />
                </label>
                <p className="text-xs text-white/50 text-center mt-2">Max 2MB • JPG, PNG, GIF</p>
              </div>

              <div className="text-center text-sm text-white/60 mb-4">Or choose an emoji</div>

              {/* Emoji Grid */}
              <div className="grid grid-cols-8 gap-2 max-h-64 overflow-y-auto p-2">
                {AVATAR_EMOJIS.map((emoji) => (
                  <button
                    key={emoji}
                    onClick={() => {
                      setSelectedEmoji(emoji);
                      setAvatarType('emoji');
                    }}
                    className={`w-12 h-12 rounded-lg text-2xl flex items-center justify-center transition-all ${
                      avatarType === 'emoji' && selectedEmoji === emoji
                        ? 'bg-green-600/30 border-2 border-green-500 scale-110'
                        : 'bg-white/5 hover:bg-white/10 border border-white/10'
                    }`}
                  >
                    {emoji}
                  </button>
                ))}
              </div>

              <div className="text-center text-sm text-white/60 mt-6 mb-4">Or choose a character</div>

              <div className="grid grid-cols-4 sm:grid-cols-6 gap-3 max-h-64 overflow-y-auto p-2">
                {CHARACTER_AVATARS.map((a) => (
                  <button
                    key={a.url}
                    onClick={() => {
                      setSelectedCharacterUrl(a.url);
                      setAvatarType('character');
                    }}
                    className={`aspect-square rounded-xl overflow-hidden border transition ${
                      avatarType === 'character' && selectedCharacterUrl === a.url
                        ? 'border-green-500 ring-2 ring-green-500/40'
                        : 'border-white/10 hover:border-white/30'
                    }`}
                    title={a.label}
                    type="button"
                  >
                    <img src={a.url} alt={a.label} className="w-full h-full object-cover" loading="lazy" />
                  </button>
                ))}
              </div>
            </>
          ) : (
            <>
              {/* Display Name */}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-white mb-2">
                  Display Name
                </label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Enter your name"
                  maxLength={50}
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-green-500/50"
                />
              </div>
              {/* Email (readonly) */}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-white mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  readOnly
                  className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/10 text-white/60 placeholder-white/50 focus:outline-none"
                />
              </div>
              {/* Location */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-white mb-2">
                  Location
                </label>
                <input
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="City, Country"
                  maxLength={100}
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-green-500/50"
                />
              </div>
              {/* Preview */}
              <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-2xl overflow-hidden flex-shrink-0">
                    {avatarType === 'emoji' ? (
                      selectedEmoji
                    ) : avatarType === 'character' && selectedCharacterUrl ? (
                      <img src={selectedCharacterUrl} alt="Avatar" className="w-full h-full object-cover" />
                    ) : uploadedImage ? (
                      <img src={uploadedImage} alt="Avatar" className="w-full h-full object-cover" />
                    ) : (
                      <User className="w-6 h-6 text-white/30" />
                    )}
                  </div>
                  <div>
                    <div className="font-semibold text-white">
                      {displayName || 'Your Name'}
                    </div>
                    <div className="text-xs text-white/60">
                      {email || 'your@email.com'}
                    </div>
                    <div className="text-xs text-white/40">
                      {location || 'Location'}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 p-6 border-t border-white/10">
          {step === 2 && (
            <button
              onClick={() => setStep(1)}
              disabled={saving}
              className="px-5 py-2.5 rounded-lg bg-white/5 hover:bg-white/10 text-white font-semibold transition-colors disabled:opacity-50"
            >
              Back
            </button>
          )}
          <div className="flex-1" />
          {step === 0 ? (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleClose(e);
              }}
              className="px-5 py-2.5 rounded-lg bg-white/5 hover:bg-white/10 text-white font-semibold transition-colors"
            >
              Cancel
            </button>
          ) : step === 1 ? (
            <div className="flex items-center gap-2">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleClose(e);
                }}
                className="px-5 py-2.5 rounded-lg bg-white/5 hover:bg-white/10 text-white font-semibold transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => setStep(2)}
                className="px-5 py-2.5 rounded-lg bg-green-600 hover:bg-green-700 text-white font-semibold transition-colors flex items-center gap-2"
              >
                Next
                <Check className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <>
              <button
                onClick={handleSave}
                disabled={saving || !displayName.trim()}
                className="px-5 py-2.5 rounded-lg bg-green-600 hover:bg-green-700 text-white font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {saving ? 'Saving...' : 'Complete Setup'}
                <Check className="w-4 h-4" />
              </button>

              <button
                onClick={() => {
                  handleClose();
                  navigate('/discover/profile');
                }}
                disabled={saving}
                className="ml-3 px-5 py-2.5 rounded-lg bg-white/5 hover:bg-white/10 text-white font-semibold transition-colors disabled:opacity-50"
              >
                Open Profile
              </button>

              {isLoggedIn && (
                <button
                  onClick={handleLogout}
                  className="ml-3 px-5 py-2.5 rounded-lg bg-red-600 hover:bg-red-700 text-white font-semibold transition-colors flex items-center gap-2"
                >
                  Log Out
                  <LogOut className="w-4 h-4" />
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
